import { create, StateCreator } from "zustand"
import { devtools, persist } from "zustand/middleware"
import { idbJSONStorage } from './idbStorage'
import type {
   ModelsStoreState,
   RawModel,
   GammeData,
   RawCollection,
   Couleurs,
   Univers,
   Cycles,
   Devises,
   Etapes,
   Familles,
   Genres,
   Portes,
   Typologies,
   PrixLigne,
   TarifRegions,
   TarifCurrencies,
   TarifTrads,
} from "../types"

// ─── Types ────────────────────────────────────────────────────────────────────

interface ModelsStoreActions {
   // Tarifs régions
   setTarifRegions: (list: Record<string, TarifCurrencies>) => void
   setPreloadOnTarifRegion: (name: string, newState: boolean) => void
   setInListOnTarifRegion: (name: string, newState: boolean) => void

   // Tarifs prix
   setTarifs: (name: string, list: PrixLigne[]) => void
   emptyTarifs: () => void
   emptyTarifsChecksums: () => void
   setPriceKeys: (list: unknown) => void

   // Listes
   setCollections: (list: Record<string, GammeData>) => void
   setSousCollections: (list: Record<string, RawCollection>) => void
   setRetailCollections: (list: Record<string, GammeData>) => void
   setCouleurs: (list: Couleurs) => void
   setUnivers: (list: Univers) => void
   setCycles: (list: Cycles) => void
   setDevises: (list: Devises) => void
   setEtapes: (list: Etapes) => void
   setFamilles: (list: Familles) => void
   setRetailFamilles: (list: Familles) => void
   setGenres: (list: Genres) => void
   setModels: (list: Record<string, RawModel>) => void
   setRetailModels: (list: Record<string, RawModel>) => void
   setPortes: (list: Portes) => void
   setTypologies: (list: Typologies) => void
   setTarifTrads: (list: TarifTrads) => void

   // Checksums
   setChecksumCollections: (val: string | null) => void
   setChecksumSousCollections: (val: string | null) => void
   setChecksumRetailCollections: (val: string | null) => void
   setChecksumCouleurs: (val: string | null) => void
   setChecksumUnivers: (val: string | null) => void
   setChecksumCycles: (val: string | null) => void
   setChecksumDevises: (val: string | null) => void
   setChecksumEtapes: (val: string | null) => void
   setChecksumFamilles: (val: string | null) => void
   setChecksumRetailFamilles: (val: string | null) => void
   setChecksumGenres: (val: string | null) => void
   setChecksumModels: (val: string | null) => void
   setChecksumRetailModels: (val: string | null) => void
   setChecksumPortes: (val: string | null) => void
   setChecksumTypologies: (val: string | null) => void
   setChecksumTarifTrads: (val: string | null) => void
   setChecksumTarifRegions: (val: string | null) => void
   setChecksumsTarifs: (name: string, val: string) => void
}

type ModelsStore = ModelsStoreState & ModelsStoreActions

// ─── État initial ─────────────────────────────────────────────────────────────

const initialState: ModelsStoreState = {
   models: {},
   retail_models: {},
   tarifs: {},

   collections: {},
   sous_collections: {},
   retail_collections: {},
   couleurs: {},
   univers: {},
   cycles: {},
   devises: {},
   etapes: {},
   familles: {},
   retail_familles: {},
   genres: {},
   portes: {},
   typologies: {},
   tarif_regions: {},
   tarif_trads: {},

   checksum_collections: null,
   checksum_sous_collections: null,
   checksum_retail_collections: null,
   checksum_couleurs: null,
   checksum_univers: null,
   checksum_cycles: null,
   checksum_devises: null,
   checksum_etapes: null,
   checksum_familles: null,
   checksum_retail_familles: null,
   checksum_genres: null,
   checksum_models: null,
   checksum_retail_models: null,
   checksum_portes: null,
   checksum_typologies: null,
   checksum_tarif_regions: null,
   checksum_tarif_trads: null,
   checksums_tarifs: null,
}

// ─── Store ────────────────────────────────────────────────────────────────────

const modelsStore: StateCreator<ModelsStore> = (set) => ({
   ...initialState,

   setTarifRegions: (list) => { // [CodeTag: MOD_1]
      if (list && typeof list === 'object') {
         return set((state) => {
            // Conserve lastused/in_list/preload des régions déjà connues
            const newlist = Object.keys(list).reduce<TarifRegions>((prev, key) => ({
               ...prev,
               [key]: {
                  currencies: list[key],
                  lastused: state.tarif_regions[key]?.lastused ?? null,
                  in_list: state.tarif_regions[key]?.in_list ?? false,
                  preload: state.tarif_regions[key]?.preload ?? false,
               },
            }), {})
            return { tarif_regions: newlist }
         })
      }
   },

   setPreloadOnTarifRegion: (name, newState) => { // [CodeTag: MOD_2]
      return set((state) => ({
         tarif_regions: Object.keys(state.tarif_regions).reduce<TarifRegions>((prev, key) => ({
            ...prev,
            [key]: {
               ...state.tarif_regions[key],
               in_list: key === name
                  ? (newState ? newState : state.tarif_regions[key].in_list)  // Force in_list si preload === true
                  : state.tarif_regions[key].in_list,
               preload: key === name ? newState : state.tarif_regions[key].preload,
            },
         }), {}),
      }))
   },

   setInListOnTarifRegion: (name, newState) => {
      return set((state) => ({
         tarif_regions: Object.keys(state.tarif_regions).reduce<TarifRegions>((prev, key) => ({
            ...prev,
            [key]: {
               ...state.tarif_regions[key],
               in_list: key === name ? newState : state.tarif_regions[key].in_list,
               preload: key === name
                  ? (!newState ? newState : state.tarif_regions[key].preload)  // Force preload à false si in_list === false
                  : state.tarif_regions[key].preload,
            },
         }), {}),
      }))
   },

   setTarifs: (name, list) =>
      set((state) => ({
         tarifs: { ...state.tarifs, [name]: list },
      })),

   emptyTarifs: () =>
      set(() => ({ tarifs: {} })),

   emptyTarifsChecksums: () =>
      set(() => ({ checksums_tarifs: null })),

   setPriceKeys: (list) =>
      set(() => ({ price_keys: list } as Partial<ModelsStore>)),

   setCollections: (list) =>       // Les collections sont réparties dans des gammes (HJO, JO, ACC, AUT)
      set(() => ({ collections: list })),

   setSousCollections: (list) =>
      set(() => ({ sous_collections: list })),

   setRetailCollections: (list) =>
      set(() => ({ retail_collections: list })),

   setCouleurs: (list) =>
      set(() => ({ couleurs: list })),

   setUnivers: (list) =>
      set(() => ({ univers: list })),

   setCycles: (list) =>
      set(() => ({ cycles: list })),

   setDevises: (list) =>
      set(() => ({ devises: list })),

   setEtapes: (list) =>
      set(() => ({ etapes: list })),

   setFamilles: (list) =>
      set(() => ({ familles: list })),

   setRetailFamilles: (list) =>
      set(() => ({ retail_familles: list })),

   setGenres: (list) =>
      set(() => ({ genres: list })),

   setModels: (list) =>
      set(() => ({ models: list })),

   setRetailModels: (list) =>
      set(() => ({ retail_models: list })),

   setPortes: (list) =>
      set(() => ({ portes: list })),

   setTypologies: (list) =>
      set(() => ({ typologies: list })),

   setTarifTrads: (list) =>
      set(() => ({ tarif_trads: list })),

   setChecksumCollections: (val) =>
      set(() => ({ checksum_collections: val })),

   setChecksumSousCollections: (val) =>
      set(() => ({ checksum_sous_collections: val })),

   setChecksumRetailCollections: (val) =>
      set(() => ({ checksum_retail_collections: val })),

   setChecksumCouleurs: (val) =>
      set(() => ({ checksum_couleurs: val })),

   setChecksumUnivers: (val) =>
      set(() => ({ checksum_univers: val })),

   setChecksumCycles: (val) =>
      set(() => ({ checksum_cycles: val })),

   setChecksumDevises: (val) =>
      set(() => ({ checksum_devises: val })),

   setChecksumEtapes: (val) =>
      set(() => ({ checksum_etapes: val })),

   setChecksumFamilles: (val) =>
      set(() => ({ checksum_familles: val })),

   setChecksumRetailFamilles: (val) =>
      set(() => ({ checksum_retail_familles: val })),

   setChecksumGenres: (val) =>
      set(() => ({ checksum_genres: val })),

   setChecksumModels: (val) =>
      set(() => ({ checksum_models: val })),

   setChecksumRetailModels: (val) =>
      set(() => ({ checksum_retail_models: val })),

   setChecksumPortes: (val) =>
      set(() => ({ checksum_portes: val })),

   setChecksumTypologies: (val) =>
      set(() => ({ checksum_typologies: val })),

   setChecksumTarifTrads: (val) =>
      set(() => ({ checksum_tarif_trads: val })),

   setChecksumTarifRegions: (val) =>
      set(() => ({ checksum_tarif_regions: val })),

   setChecksumsTarifs: (name, val) =>
      set((state) => ({
         checksums_tarifs: { ...(state.checksums_tarifs ?? {}), [name]: val },
      })),
})

const useModelsStore = create<ModelsStore>()(devtools(persist(modelsStore, {
   name: "models",
   storage: idbJSONStorage,
   version: 1,
   // v1 (réparation one-shot) : corrige la désync « checksum présent mais listes vides »
   // (catalogue vide). On invalide tous les checksums → re-fetch complet des listes au
   // prochain login. NE SE REJOUE PAS (la version est stockée dans le persist ; migrate
   // n'est appelé que si version stockée < version config). Préserve tarif_regions & co.
   migrate: (persisted: any) => {
      if (persisted && typeof persisted === 'object') {
         for (const k of Object.keys(persisted))
            if (k.startsWith('checksum_') || k.startsWith('checksums_')) persisted[k] = null
      }
      return persisted
   },
})))

/**
 * Attend la fin de l'hydratation Zustand du store `models` (lecture IndexedDB async).
 * À appeler AVANT toute écriture issue d'un fetch initial (initialFetch) : sinon
 * l'hydratation tardive rejoue l'ancien snapshot persisté (potentiellement vide) et
 * écrase les données fraîchement posées — typiquement tarif_regions qui se retrouvait
 * vide au 1er login jusqu'à un reload. Même garde que awaitPrefsHydrated() côté prefs.
 */
export function awaitModelsHydrated(): Promise<void> {
   if (useModelsStore.persist.hasHydrated()) return Promise.resolve()
   return new Promise<void>((resolve) => {
      const unsub = useModelsStore.persist.onFinishHydration(() => {
         unsub()
         resolve()
      })
   })
}

export default useModelsStore
export type { ModelsStore, ModelsStoreActions }
